// ============================================================================
// HAI Reporting Tool - Simple Entry Layout
// Minimal layout for data entry users (Ward nurses, IPC nurses, etc.)
// ============================================================================

import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { SyncStatusIndicator } from '@/components/ui/SyncStatusIndicator';
import { syncService } from '@/services/sync.service';

interface SimpleEntryLayoutProps {
  children: ReactNode;
}

export function SimpleEntryLayout({ children }: SimpleEntryLayoutProps) {
  const { userProfile, signOut } = useAuth();
  const navigate = useNavigate();

  // Initialize sync service
  useEffect(() => {
    syncService.initialize();
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Simple Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo/Title */}
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-primary-600">
                HAI Data Entry
              </h1>
            </div>

            {/* User Info & Actions */}
            <div className="flex items-center gap-4">
              {/* Sync Status Indicator */}
              <SyncStatusIndicator />

              <div className="hidden sm:block text-sm">
                <p className="font-medium text-gray-900">
                  {userProfile?.displayName}
                </p>
                <p className="text-xs text-gray-500">
                  {userProfile?.role.replace('_', ' ')}
                </p>
              </div>

              <button
                onClick={handleSignOut}
                className="btn btn-secondary text-sm"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Simple Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-gray-500">
            HAI Reporting Tool • POPIA Compliant • No Patient Identifiable Data
          </p>
        </div>
      </footer>
    </div>
  );
}
